/* ──────────────────────────────────────────────────────────────────────────
 * USD/CAD exchange rate loader — the single read path for foreign exchange
 * data the simulator uses to convert USD-listed ticker values into CAD.
 *
 * Companion to ticker-loader.ts and rates-loader.ts. Same architectural
 * shape: async function returning a snapshot, reading from a committed
 * JSON cache today, swappable to runtime fetch later without touching
 * anything else.
 *
 * Source: Yahoo Finance "CAD=X" daily USD/CAD spot rate. Coverage from
 * ~2003-09 to present. Refreshed manually via `npm run refresh-fx`.
 *
 * **Norbert's Gambit assumption.** The simulator assumes Canadian
 * leveraged investors execute their CAD→USD conversion via Norbert's
 * Gambit — buying a Canadian-listed interlisted stock, journalling to
 * the US side, and selling for USD. This conversion happens at the
 * interbank rate with no commission, modelled as the published daily
 * spot rate. Users who instead convert through retail brokerage FX
 * (typically a 1.5-2.5% spread on top of the interbank rate) experience
 * worse real-world results than the simulator implies. See MATH.md.
 *
 * Forward-fill semantics: weekends and holidays carry forward the most
 * recent published rate. Same pattern as the Prime rate loader. The
 * `usdCadRateAt(series, date)` helper handles the lookup.
 * ────────────────────────────────────────────────────────────────────────── */

import usdCadData from "./usd-cad-rate.json";

// ── Types ──────────────────────────────────────────────────────────────────

export type UsdCadObservation = {
  /** ISO YYYY-MM-DD date. */
  readonly date: string;
  /** CAD per 1 USD as a decimal (e.g., 1.3752 means 1 USD = 1.3752 CAD). */
  readonly rate: number;
};

export type UsdCadMeta = {
  readonly source: string;
  readonly symbol: string;
  readonly label: string;
  readonly firstDate: string;
  readonly lastDate: string;
  readonly observationCount: number;
  readonly generatedAt: string;
};

export type UsdCadSeries = {
  readonly meta: UsdCadMeta;
  readonly observations: readonly UsdCadObservation[];
};

// ── Loader ─────────────────────────────────────────────────────────────────

/**
 * Load the full USD/CAD daily exchange-rate series.
 */
export async function loadUsdCadRates(): Promise<UsdCadSeries> {
  return usdCadData as UsdCadSeries;
}

// ── Lookup helper ──────────────────────────────────────────────────────────

/**
 * Forward-fill lookup: return the CAD-per-USD rate from the most recent
 * observation whose date is ≤ targetDate.
 *
 * Weekends and holidays carry the most recent published rate. The
 * simulator treats this as the "rate in effect" on any given day, which
 * matches how a conversion executed on a weekend would actually settle
 * (at Monday's rate).
 *
 * If targetDate is after the last observation, returns the last rate
 * (carries forward indefinitely). If targetDate is before the first
 * observation, throws — the simulator shouldn't run USD-ticker scenarios
 * with windows pre-dating the available FX data (the page should clamp
 * the window first; see the "use what we have" rule).
 */
export function usdCadRateAt(series: UsdCadSeries, targetDate: string): number {
  const obs = series.observations;
  if (obs.length === 0) {
    throw new Error("usdCadRateAt: empty observation series");
  }
  if (targetDate < obs[0]!.date) {
    throw new Error(
      `usdCadRateAt: date ${targetDate} is before first observation ${obs[0]!.date}. ` +
        `Clamp the simulation window to start no earlier than ${obs[0]!.date} for USD tickers.`,
    );
  }

  // Binary search for the largest index where obs[idx].date ≤ targetDate.
  let lo = 0;
  let hi = obs.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (obs[mid]!.date <= targetDate) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return obs[lo]!.rate;
}
